import type {
  CategoryId,
  RFPack,
  RFPContext,
  PackSection,
  SectionId,
  ComplianceMatrixRow,
  ScorecardRow,
  GapItem,
  QualityReport,
  PackGrade,
  RubricAnchor,
  ManualSections,
} from "./types";
import { getPlaybook, isKnownCategory, categoryLabel, categoryDomain, servicesSubdomain, type ServicesSubdomain } from "./playbooks";
import { getLegalStubs } from "./legalStubs";
import { getAdminClauses } from "./adminClauses";
import type { AdminClause } from "./adminClauses";
import { runConsistencyChecks, gradePack } from "./consistencyEngine";
import { emptyContext } from "./types";
import { sanitizeContextText, normalizeContractTerm, summarizeVolumes, sanitizeBuyerName, sanitizeFinalText, purgeLogisticsTerms, stripBoldMarkers } from "./contextSanitize";

// ─── Utilities ────────────────────────────────────────────────────────────────

const TBD = "[TBD]";

/**
 * Clean professional stand-ins for metadata the buyer omits. These render in
 * place of a raw [TBD] tag so Section 1 never ships a bare placeholder; the
 * values remain overridable in the context drawer and the gaps ledger still
 * prompts the buyer to confirm them.
 */
const METADATA_FALLBACKS = {
  strategicImportance: "High — critical operational dependency",
  incumbent: "Confidential / Disclosed at NDA stage",
  contractEnd: "Subject to transition timeline",
};

function lines(arr: string[] | undefined): string {
  const src = Array.isArray(arr) ? arr.filter((x) => x && x.trim()) : [];
  return src.length ? src.map((x) => `- ${x}`).join("\n") : `- ${TBD}`;
}

/** Inline comma-joined list (for labels like "Systems in scope"), or TBD. */
function joinList(arr: string[] | undefined): string {
  const src = Array.isArray(arr) ? arr.filter((x) => x && x.trim()) : [];
  return src.length ? src.join(", ") : TBD;
}

function maybe(value: string | null | undefined): string {
  return value && value.trim() ? value : TBD;
}

function maybeYesNo(v: boolean | null): string {
  return v === true ? "Yes" : v === false ? "No" : TBD;
}

// ─── Response deadline & suggested response time ─────────────────────────────

/** Response-time guidance (draft + 1 revise round) contributed by the category
 *  playbook. Never an SLA for the buyer — labelled "guidance — confirm". */
const RESPONSE_TIME_GUIDANCE: Record<string, string> = {
  it: "approximately 40 hours across 15 calendar days",
  marketing: "approximately 24 hours across 10 calendar days",
  professional: "approximately 16 hours across 10 calendar days",
  supply_chain: "approximately 40 hours across 15 calendar days",
  generic: "approximately 30 hours across 15 calendar days",
};

function suggestedResponseTime(isServices: boolean, subdomain: ServicesSubdomain): string {
  const key = isServices ? subdomain : "supply_chain";
  return RESPONSE_TIME_GUIDANCE[key] ?? RESPONSE_TIME_GUIDANCE.generic;
}

/** How the response deadline renders. Dates are always explicit buyer inputs
 *  (Issue Date / Response Date pickers on the wizard) — never derived from
 *  go-live, never labelled "(suggested — confirm)". Empty = [TBD] so the gaps
 *  register keeps prompting until set. */
function responseDeadlineLine(ctx: RFPContext): string {
  return maybe(ctx.process.deadline);
}

/** Human-readable time zone label for the submission deadline sentence. */
function tzLabel(zone: string | undefined | null): string {
  const m = /America\/(.+)/.exec(zone ?? "");
  if (m) {
    const area = m[1];
    if (/Eastern/.test(area)) return "ET";
    if (/Central/.test(area)) return "CT";
    if (/Mountain/.test(area)) return "MT";
    if (/Pacific/.test(area)) return "PT";
    if (/Arizona/.test(area)) return "MST";
    return "US time";
  }
  if (/Europe\/London/.test(zone ?? "")) return "UK time";
  if (/Europe\/(.+)/.test(zone ?? "")) return "CET";
  return "";
}

/**
 * The authoritative submission instruction: exact time + zone + date, e.g.
 * "All proposals must be submitted electronically no later than 17:00 ET on
 * 4 October 2026." Time and date are the buyer's explicit wizard inputs.
 */
function submissionDeadlineSentence(ctx: RFPContext): string {
  const datePart = responseDeadlineLine(ctx);
  if (datePart === TBD) return TBD;
  const time = ctx.process.submission_time?.trim() || "17:00";
  const tz = tzLabel(ctx.process.timezone);
  return tz
    ? `All proposals must be submitted electronically no later than ${time} ${tz} on ${datePart}.`
    : `All proposals must be submitted electronically no later than ${time} on ${datePart}.`;
}

/** Appendix overview for Section 13 — a plain "what it is / who completes it /
 *  when it matters" walkthrough so each appendix has exactly one owner and one
 *  moment in the process (consultant feedback). */
function appendicesGuide(): string {
  const rows: Array<[string, string, string]> = [
    ["A", "Business context & client memo", "Owner: client / procurement. Action: issue alongside the RFP as background — not a bidder submission."],
    ["B", "Scorecard & evaluation weights", "Internal Use — use to evaluate and score bidder proposals (Scorecard.xlsx)."],
    ["C", "Detailed compliance matrix (full)", "Action — issue separately to bidders for itemized technical compliance responses."],
    ["D", "Scoring rubric (1 / 3 / 5 anchors)", "Internal Use — reference the evidence-quality scale when scoring every compliance claim."],
    ["E", "Change & variation register", "Owner: client, throughout — audit trail for any scope change agreed during the process."],
    ["F", "Contracting responses", "Action — include in the bidder submission pack for legal redlines (MSA deviations, DPA sub-processors)."],
    ["G", "Insurance requirements & certificates", "Action — bidders submit evidence before award."],
    ["H", "Reference check", "Internal Use — structured reference follow-up on Section 4 claims, before award."],
    ["I", "Change & variation request (stub)", "Owner: both parties, in term."],
    ["J", "Pricing template / category rate card", "Action — issue separately to bidders for structured commercial bidding (never merge with the PDF)."],
    ["K", "Glossary", "Owner: everyone — shared vocabulary."],
  ];
  const intro =
    "## How to use the appendices\n\nThis pack keeps the working detail out of the body so the document stays readable. Each appendix below carries one owner and one action — who produces it and what happens to it at its moment in the process:\n\n";
  const list = rows
    .map(([id, name, action]) => `- **Appendix ${id} — ${name}:** ${action}`)
    .join("\n");
  return `${intro}${list}\n\nAll other gaps and assumptions are tracked in Gaps_and_Assumptions.xlsx.`;
}

export function countTbd(text: string): number {
  return (text.split("[TBD]").length - 1) + (text.split("[PLAYBOOK DEFAULT").length - 1);
}

// ─── Normalisation: build a pack context from a user-supplied Partial ───────

export function buildContext(category: CategoryId, base: Partial<RFPContext>): RFPContext {
  const ctx = emptyContext(category);
  const pbk = getPlaybook(category, base.meta?.custom_category_name);

  const c = base;
  ctx.meta.pack_id = c.meta?.pack_id ?? ctx.meta.pack_id;
  // Entity names are sanitized globally so a draft/backend "Who …" phrase can
  // never bind as the buyer on the cover, Section 3, Section 11 or headers.
  ctx.meta.client_name = sanitizeBuyerName(c.meta?.client_name) || sanitizeBuyerName(ctx.meta.client_name) || ctx.meta.client_name || "";
  ctx.meta.buyer_entity = sanitizeBuyerName(c.meta?.buyer_entity) || sanitizeBuyerName(ctx.meta.buyer_entity) || ctx.meta.buyer_entity || "";
  ctx.meta.custom_category_name = c.meta?.custom_category_name?.trim() ? sanitizeContextText(c.meta.custom_category_name) : ctx.meta.custom_category_name;
  ctx.meta.geography = c.meta?.geography?.length ? c.meta.geography : ctx.meta.geography;
  ctx.meta.currency = c.meta?.currency ?? ctx.meta.currency;
  ctx.meta.language = c.meta?.language ?? ctx.meta.language;
  ctx.meta.confidentiality = c.meta?.confidentiality ?? ctx.meta.confidentiality;

  ctx.problem.current_state = sanitizeContextText(c.problem?.current_state ?? ctx.problem.current_state);
  ctx.problem.current_state_constraints = sanitizeContextText(c.problem?.current_state_constraints ?? ctx.problem.current_state_constraints);
  ctx.problem.business_problem = sanitizeContextText(c.problem?.business_problem ?? ctx.problem.business_problem);
  ctx.problem.desired_outcome = sanitizeContextText(c.problem?.desired_outcome ?? ctx.problem.desired_outcome);
  ctx.problem.success_metrics = c.problem?.success_metrics?.length ? c.problem.success_metrics : ctx.problem.success_metrics;
  ctx.problem.strategic_importance = sanitizeContextText(c.problem?.strategic_importance ?? ctx.problem.strategic_importance);
  if (c.problem?.incumbent?.notes) {
    ctx.problem.incumbent.notes = sanitizeContextText(c.problem.incumbent.notes);
    ctx.problem.incumbent.exists = c.problem.incumbent.exists ?? null;
    if (c.problem.incumbent.end_date) ctx.problem.incumbent.end_date = c.problem.incumbent.end_date;
  }

  // Scope: user list first, then playbook seeds appended (not overwritten).
  const pbkIn = pbk.scope_seeds.in_scope ?? [];
  ctx.scope.in_scope = dedupe([...(c.scope?.in_scope ?? []), ...pbkIn]);
  ctx.scope.out_of_scope = dedupe([...(c.scope?.out_of_scope ?? []), ...(pbk.scope_seeds.out_of_scope ?? [])]);
  ctx.scope.sites = c.scope?.sites?.length ? c.scope.sites : ctx.scope.sites;
  ctx.scope.volumes = summarizeVolumes(c.scope?.volumes ?? ctx.scope.volumes);
  ctx.scope.systems = c.scope?.systems?.length ? c.scope.systems : ctx.scope.systems;
  ctx.scope.services = c.scope?.services?.length ? c.scope.services : ctx.scope.services;
  ctx.scope.exclusions = c.scope?.exclusions?.length ? c.scope.exclusions : ctx.scope.exclusions;

  ctx.constraints.budget = c.constraints?.budget ?? ctx.constraints.budget;
  ctx.constraints.go_live = c.constraints?.go_live ?? ctx.constraints.go_live;
  ctx.constraints.contract_term_months = normalizeContractTerm(
    c.constraints?.contract_term_months ?? ctx.constraints.contract_term_months,
  );
  ctx.constraints.contract_extensions = c.constraints?.contract_extensions ?? "";
  ctx.constraints.must_keep = c.constraints?.must_keep?.length ? c.constraints.must_keep : [];
  ctx.constraints.cannot_change = c.constraints?.cannot_change?.length ? c.constraints.cannot_change : [];
  ctx.constraints.regulatory = c.constraints?.regulatory?.length ? c.constraints.regulatory : [];
  ctx.constraints.headcount_rules = c.constraints?.headcount_rules ?? "";

  ctx.stakeholders.sponsor = c.stakeholders?.sponsor ?? "";
  ctx.stakeholders.procurement_lead = c.stakeholders?.procurement_lead ?? "";
  ctx.stakeholders.technical_lead = c.stakeholders?.technical_lead ?? "";
  ctx.stakeholders.ops_lead = c.stakeholders?.ops_lead ?? "";
  ctx.stakeholders.legal = c.stakeholders?.legal ?? "";
  ctx.stakeholders.contact_email = c.stakeholders?.contact_email ?? "";

  ctx.commercial.pricing_model_preference = c.commercial?.pricing_model_preference?.length ? c.commercial.pricing_model_preference : [];
  ctx.commercial.tco_years = c.commercial?.tco_years ?? ctx.commercial.tco_years;
  ctx.commercial.payment_terms = c.commercial?.payment_terms ?? ctx.commercial.payment_terms;
  ctx.commercial.price_validity_days = c.commercial?.price_validity_days ?? ctx.commercial.price_validity_days;
  ctx.commercial.should_cost_notes = c.commercial?.should_cost_notes ?? "";
  ctx.commercial.tax_exclusive = c.commercial?.tax_exclusive ?? ctx.commercial.tax_exclusive;
  ctx.commercial.price_fix_months = c.commercial?.price_fix_months ?? ctx.commercial.price_fix_months;
  ctx.commercial.volume_guaranteed = c.commercial?.volume_guaranteed ?? ctx.commercial.volume_guaranteed;
  ctx.commercial.pricing_basis = c.commercial?.pricing_basis ?? ctx.commercial.pricing_basis;
  ctx.commercial.pricing_rules = c.commercial?.pricing_rules?.length ? c.commercial.pricing_rules.slice(0, 8) : ctx.commercial.pricing_rules;

  ctx.evaluation.weights = mergeEvaluationWeights(ctx.evaluation.weights, c.evaluation?.weights);
  ctx.evaluation.weight_names = { ...(ctx.evaluation.weight_names ?? {}), ...(c.evaluation?.weight_names ?? {}) };
  ctx.evaluation.mandatory_gates = c.evaluation?.mandatory_gates?.length ? c.evaluation.mandatory_gates : [];
  ctx.evaluation.interviews = c.evaluation?.interviews ?? true;
  ctx.evaluation.negotiation = c.evaluation?.negotiation ?? true;
  // Never default to an invented bidder count ("3-5"). Empty renders as
  // "to be determined by the buyer" in Section 12.
  ctx.evaluation.supplier_count_target = c.evaluation?.supplier_count_target?.trim() ? c.evaluation.supplier_count_target.trim() : "";
  ctx.evaluation.award_model = c.evaluation?.award_model ?? ctx.evaluation.award_model;
  ctx.evaluation.weights_reviewed = c.evaluation?.weights_reviewed ?? ctx.evaluation.weights_reviewed;

  ctx.process = { ...ctx.process, ...c.process };
  ctx.legal_flags = { ...ctx.legal_flags, ...c.legal_flags };
  if (c.legal_flags?.liability_notes?.length) ctx.legal_flags.liability_notes = c.legal_flags.liability_notes.slice(0, 8);

  ctx.source_files = c.source_files?.length ? c.source_files : [];
  ctx.vendor_questions = c.vendor_questions?.length ? c.vendor_questions.slice(0, 12) : [];

  return ctx;
}

function dedupe<T>(arr: T[]): T[] {
  return Array.from(new Set(arr.filter((x) => x && String(x).trim())));
}

/**
 * The Appendix L (Vendor Questionnaire) seed, extended with any structured
 * "Ask:" questions routed out of the internal strategy notes. Extracted
 * questions get their own worksheet section so the workbook export carries
 * them without touching the playbook defaults.
 */
function vendorQuestionnaireWithAsks(ctx: RFPContext, pbk: ReturnType<typeof getPlaybook>): NonNullable<ReturnType<typeof getPlaybook>["vendor_questionnaire"]> {
  const base = pbk.vendor_questionnaire ? pbk.vendor_questionnaire.map((s) => ({ name: s.name, questions: [...s.questions] })) : [];
  const asks = (ctx.vendor_questions ?? []).filter(Boolean);
  if (!asks.length) return base;
  return [
    ...base,
    {
      name: "Buyer clarification questions (from briefing)",
      questions: asks.map((q, i) => ({ id: `BVQ-${String(i + 1).padStart(2, "0")}`, question: q })),
    },
  ];
}

// ─── Matrix assembly ──────────────────────────────────────────────────────────

export function buildMatrix(ctx: RFPContext): ComplianceMatrixRow[] {
  const pbk = getPlaybook(ctx.meta.category_id, ctx.meta.custom_category_name, ctxBrief(ctx));
  const userInScope = (ctx.scope.in_scope ?? []).join(" ").toLowerCase();

  return pbk.matrix.flatMap((seed) => {
    const inScope =
      seed.scope_tags.includes("all") ||
      seed.scope_tags.some((t) => userInScope.includes(t));
    if (!inScope) return [];
    return [{
      id: seed.id,
      category: seed.category,
      requirement: seed.requirement,
      mandatory: seed.mandatory,
      supplier_response: "tbd" as const,
      evidence: seed.evidence,
      maps_to: seed.maps_to,
    }];
  });
}

// ─── SLA overrides (draft > playbook) ────────────────────────────────────────

/**
 * SLA targets can come from the drafted SLAs, not just the playbook defaults.
 * Draft-stated numbers always win over the playbook baseline (Draft > Playbook),
 * so REQ-OPS-004/006/019 and the Section 8 catalogue reflect the buyer's own
 * contract values instead of the category defaults (98% / 99.5% / 48h).
 */
export function extractSlaOverrides(text: string): { otif?: string; inventoryAccuracy?: string; returnsHours?: string } {
  const t = text.replace(/\s+/g, " ").toLowerCase();
  const out: { otif?: string; inventoryAccuracy?: string; returnsHours?: string } = {};
  const otif = /otif[^0-9]{0,50}?([0-9]{2}(?:\.[0-9]+)?)\s*%/.exec(t);
  if (otif) out.otif = otif[1];
  const inv = /inventory accuracy[^0-9]{0,50}?([0-9]{2}(?:\.[0-9]+)?)\s*%/.exec(t);
  if (inv) out.inventoryAccuracy = inv[1];
  const ret = /returns?[^.!?\n]{0,60}?(?:within|in|≤|<=|=)\s*([0-9]{1,3})\s*hours?/.exec(t);
  if (ret) out.returnsHours = ret[1];
  return out;
}

function slaOverrideText(ctx: RFPContext): string {
  return [
    ...(ctx.problem.success_metrics ?? []),
    ctx.problem.desired_outcome,
    ctx.problem.current_state,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
}

/** Compliance matrix with any draft-stated SLA targets applied. */
export function matrixWithOverrides(ctx: RFPContext): ComplianceMatrixRow[] {
  const rows = buildMatrix(ctx).map((r) => ({ ...r }));
  const ov = extractSlaOverrides(slaOverrideText(ctx));
  if (!ov.otif && !ov.inventoryAccuracy && !ov.returnsHours) return rows;
  return rows.map((r) => {
    let req = r.requirement;
    if (ov.otif && r.id === "REQ-OPS-004")
      req = req.replace(/OTIF\s*[≥>]=?\s*[0-9]+(?:\.[0-9]+)?\s*%/, `OTIF ≥ ${ov.otif}%`);
    if (ov.inventoryAccuracy && r.id === "REQ-OPS-006")
      req = req.replace(/Inventory accuracy\s*[≥>]=?\s*[0-9]+(?:\.[0-9]+)?\s*%/, `Inventory accuracy ≥ ${ov.inventoryAccuracy}%`);
    if (ov.returnsHours && r.id === "REQ-OPS-019")
      req = req.replace(/within\s*[0-9]+\s*hours?/, `within ${ov.returnsHours} hours`);
    return req === r.requirement ? r : { ...r, requirement: req };
  });
}

/** Section 8 SLA catalogue with any draft-stated targets applied. */
export function catalogueWithOverrides(ctx: RFPContext) {
  const pbk = getPlaybook(ctx.meta.category_id, ctx.meta.custom_category_name, ctxBrief(ctx));
  const catalogue = (pbk.sla_catalogue ?? []).map((s) => ({ ...s }));
  const ov = extractSlaOverrides(slaOverrideText(ctx));
  if (!ov.otif && !ov.inventoryAccuracy && !ov.returnsHours) return catalogue;
  for (const s of catalogue) {
    if (/otif/i.test(s.metric) && ov.otif)
      s.target = s.target.replace(/[≥>]=?\s*[0-9]+(?:\.[0-9]+)?\s*%/, `≥ ${ov.otif}%`);
    else if (/inventory accuracy/i.test(s.metric) && ov.inventoryAccuracy)
      s.target = s.target.replace(/[≥>]=?\s*[0-9]+(?:\.[0-9]+)?\s*%/, `≥ ${ov.inventoryAccuracy}%`);
  }
  if (ov.returnsHours) {
    const returnsRow = catalogue.find((s) => /returns/i.test(s.metric));
    if (returnsRow) returnsRow.target = returnsRow.target.replace(/within\s*[0-9]+\s*hours?|≤\s*[0-9]+\s*hours?/, `≤ ${ov.returnsHours} hours from receipt`);
    else catalogue.push({ metric: "Returns processing", target: `≤ ${ov.returnsHours} hours from receipt`, evidence: "SOP + SLA history", mandatory: false });
  }
  return catalogue;
}

// ─── Services sub-domain text ─────────────────────────────────────────────────

/** Brief signal text for sub-domain playbook routing (name excluded — passed separately). */
function ctxBrief(ctx: RFPContext): string {
  return [
    ctx.problem.business_problem,
    ctx.problem.current_state,
    ctx.problem.desired_outcome,
  ].filter(Boolean).join(" ").slice(0, 400);
}

function servicesSecurityText(ctx: RFPContext, subdomain: ServicesSubdomain): string {
  const extra =
    subdomain === "marketing"
      ? "\n- Brand safety & content governance: controls for externally published deliverables confirmed with evidence.\n- Ad fraud / invalid-traffic controls: IVT monitoring and discrepancy reconciliation apply where paid media is in scope."
      : subdomain === "it"
        ? "\n- Access & identity: least-privilege access, multi-factor authentication (MFA) and change management for any buyer systems accessed.\n- Resilience: backup, disaster recovery and RPO / RTO commitments, with monitoring and periodic DR testing.\n- Data residency: storage and processing locations for buyer data confirmed."
        : "";
  return `## Data security, privacy & compliance\n\n- Personal data handled: ${
    ctx.legal_flags.personal_data === "yes"
      ? (ctx.legal_flags.personal_data_label || "Yes — GDPR / CCPA-compliant handling required")
      : ctx.legal_flags.personal_data === "no"
        ? "No"
        : TBD
  }\n- Data privacy & processing: GDPR / CCPA-compliant processing, a current processing register and sub-processor list supplied with the proposal.\n- Intellectual property: ownership of deliverables and buyer-supplied materials must be confirmed (see Section 11 and Appendix F).\n- Cybersecurity: SOC 2 / ISO 27001 (or equivalent) attestation for any systems or data accessed; vulnerability management and incident reporting confirmed.${extra}\n\n**Supply chain cyber:** confirm third-party supply chain risk controls for any sub-contracted services.`;
}

function servicesEsgText(subdomain: ServicesSubdomain): string {
  const scopeLine =
    subdomain === "marketing"
      ? "media / digital"
      : subdomain === "it"
        ? "IT / data"
        : "professional";
  const controls =
    subdomain === "it"
      ? "ethical-AI, data-governance and energy controls applicable to hosted services"
      : "ethical-AI and data-governance controls";
  return `## Requirements\n\n- Confirm labour standards and right of audit across the delivering team, including any sub-contracted capacity.\n- Modern slavery statement aligned to the statutory threshold.\n- Report your climate commitments and how this engagement supports them (Scopes 1–2, and Scope 3 where the category makes it material).\n- Confirm supplier-diversity reporting where applicable.\n- For ${scopeLine} services confirm ${controls} where relevant to the deliverables.`;
}

// ─── Scorecard ────────────────────────────────────────────────────────────────

/** Default human labels for the five seed dimensions (the scorecard keys users
 *  can rename via the weights dialog). */
const DEFAULT_WEIGHT_LABELS: Record<string, string> = {
  technical_fit: "Technical Fit",
  approach: "Approach & Solution Narrative",
  commercial: "Commercial & TCO",
  capability: "Capability & Qualifications",
  risk: "Risk & Compliance",
};

/** Prettify a raw dimension id ("technical_fit" → "Technical Fit", "custom_1"
 *  → "Custom 1") when the pack carries no explicit label for it. */
export function prettyDimensionLabel(dim: string): string {
  return dim
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase())
    .replace(/\s\w/g, (c) => c.toUpperCase());
}

/** The user-configured category name for a dimension (weight_names override)
 *  with a clean default fallback so Section 12 and the scorecard never render a
 *  raw record key. */
export function evaluationDimensionLabel(ctx: RFPContext, dim: string): string {
  const label = ctx.evaluation.weight_names?.[dim]?.trim();
  return label || DEFAULT_WEIGHT_LABELS[dim] || prettyDimensionLabel(dim);
}

/**
 * Authority rule for the scorecard-weights map. The default five dimensions
 * seed a brand-new pack; once a weights map is supplied it is treated as the
 * FULL configured set, so dimensions the user deleted never come back on
 * regenerate (a plain `{...defaults, ...user}` spread would re-insert them).
 * Default-key order is preserved, then custom keys in their stored order.
 */
export function mergeEvaluationWeights(base: Record<string, number>, user?: Record<string, number>): Record<string, number> {
  if (!user || Object.keys(user).length === 0) return { ...base };
  const merged: Record<string, number> = {};
  for (const k of Object.keys(base)) if (user[k] !== undefined) merged[k] = user[k];
  for (const k of Object.keys(user)) if (merged[k] === undefined) merged[k] = user[k];
  return merged;
}

export function buildScorecard(ctx: RFPContext): ScorecardRow[] {
  const w = ctx.evaluation.weights;
  const guides: Record<string, string> = {
    technical_fit: "Compliance matrix rows mapped to technical_fit (Section 5). Mandatory rows scored on evidence quality.",
    approach: "Solution narrative (Section 6): operating model, 180-day plan, risk mitigations.",
    commercial: "Commercial proposal (Section 7): rate card completeness, transparency, TCO over the stated term.",
    capability: "Qualifications (Section 4): references, experience, capacity, financial standing.",
    risk: "Security & compliance (Sections 9–10): evidence completeness, residual risk.",
  };
  return (Object.keys(w) as string[]).map((dim) => ({
    dimension: dim,
    weight: w[dim],
    how_scored: guides[dim] ?? "",
    label: evaluationDimensionLabel(ctx, dim),
  }));
}

// ─── Gaps register ────────────────────────────────────────────────────────────

export function buildGaps(ctx: RFPContext): GapItem[] {
  const gaps: GapItem[] = [];
  const push = (key: string, label: string, red: boolean) =>
    gaps.push({ key, label, severity: red ? "red" : "amber", status: "open" });

  // Required procurement dates (wizard-enforced; the deadline gap stays red so
  // a pack can never reach issue-ready without an explicit response date).
  if (!ctx.process.deadline) push("deadline", "Submission deadline", true);
  if (!ctx.process.issue_date) push("issue_date", "RFP issue date", false);
  if (!ctx.process.qa_deadline) push("qa_deadline", "Date questions are due", false);
  if (!ctx.process.qa_response_date) push("qa_response_date", "Date Q&A responses are issued", false);
  if (!ctx.process.presentation_date) push("presentation_date", "Presentation date", false);
  if (!ctx.process.award_target_date) push("award_target_date", "Anticipated award date", false);
  if (!ctx.problem.business_problem) push("problem", "Business problem statement", true);
  if (ctx.constraints.budget.known !== true && ctx.constraints.budget.publish_to_bidders === true)
    push("budget", "Budget to publish in document", true);
  if (!ctx.problem.incumbent.notes) push("incumbent", "Incumbent details (exists / end date)", false);
  if (!ctx.stakeholders.procurement_lead) push("procurement_lead", "Procurement contact to issue", true);
  if (!ctx.stakeholders.legal) push("legal", "Legal contact for contracting", false);
  if (!ctx.scope.volumes) push("volumes", "Volumes / throughput", false);
  if (ctx.legal_flags.personal_data === null) push("personal_data", "Personal data handling", true);
  if (ctx.commercial.price_validity_days <= 0) push("price_validity", "Price validity days", true);
  // Client commercial terms are never assumed — flagged until the buyer states
  // them. ([TBD] renders in Section 7 until then.)
  if (!ctx.commercial.payment_terms) push("payment_terms", "Client payment terms", false);
  if (!ctx.evaluation.award_model) push("award_model", "Award model (exclusive vs split)", false);
  if (!ctx.meta.buyer_entity) push("buyer_entity", "Buyer legal entity (for contract)", true);
  if (!ctx.evaluation.mandatory_gates.length) push("gates", "Mandatory gates", false);

  const weightSum = Object.values(ctx.evaluation.weights).reduce((a, b) => a + b, 0);
  if (weightSum !== 100) push("weight_sum", `Evaluation weights total ${weightSum}% — must be 100%`, true);
  if (!ctx.evaluation.weights_reviewed) push("weights_review", "Evaluation weights reviewed & confirmed", false);

  return gaps;
}

// ─── Section generators ───────────────────────────────────────────────────────

/**
 * Admin / commercial boilerplate pass (clause library).
 *
 * Inserts the versioned procurement clause set into the already-built section
 * bodies, in place: cover banner for s00, appended clause groups for the
 * response / qualifications / solution / pricing / contract / evaluation /
 * appendices sections. Runs after section generation and before the quality
 * engine, so consistency checks validate the enriched text. Never adds a
 * section and never rewrites generator text.
 */
export function applyAdminClauses(sections: PackSection[], ctx: RFPContext): void {
  const clauses = getAdminClauses(ctx);
  const bySection = new Map<SectionId, AdminClause[]>();
  for (const c of clauses) {
    const arr = bySection.get(c.section) ?? [];
    arr.push(c);
    bySection.set(c.section, arr);
  }
  for (const s of sections) {
    const items = bySection.get(s.id);
    if (!items?.length) continue;
    if (s.id === "s00_cover") {
      s.body = `${items.map((c) => c.body).join("\n\n")}\n\n${s.body}`;
    } else {
      s.body = s.body + "\n\n" + items.map((c) => `## ${c.title}\n\n${c.body}`).join("\n\n");
    }
    s.tbd_count = countTbd(s.body);
  }
}

export interface SectionOverrides {
  skipSections?: ReadonlySet<SectionId>;
  manualSections?: ManualSections;
  /** Red-gap keys explicitly waived — honoured by the issue-ready gate. */
  waivedGaps?: string[];
}

export function generatePack(ctx: RFPContext, opts?: SectionOverrides): { sections: PackSection[]; grade: PackGrade; quality: QualityReport } {
  const brief = ctxBrief(ctx);
  const pbk = getPlaybook(ctx.meta.category_id, ctx.meta.custom_category_name, brief);
  const domain = categoryDomain(ctx.meta.category_id, ctx.meta.custom_category_name, [
    ctx.problem.business_problem,
    ctx.problem.current_state,
    ctx.meta.client_name,
  ].filter(Boolean).join(" ").slice(0, 400));
  const isServices = domain === "services";
  const subdomain = servicesSubdomain(ctx.meta.category_id, ctx.meta.custom_category_name, brief);
  // Buyer name sanity: a residual "Who …" from a draft/backend phrase must
  // never render as the buyer. sanitizeBuyerName rejects non-entity words and
  // falls back to the genuinely-scrubbed client name (or [TBD]).
  const client =
    sanitizeBuyerName(ctx.meta.client_name) || sanitizeBuyerName(ctx.meta.buyer_entity) || maybe(ctx.meta.client_name) || maybe(ctx.meta.buyer_entity);
  const sections: PackSection[] = [];
  const add = (id: SectionId, num: string, title: string, body: string, citations: string[] = []) => {
    if (opts?.skipSections?.has(id)) return;
    const manual = opts?.manualSections?.[id];
    if (manual) {
      sections.push({ id, num, title: manual.title, body: manual.body, citations, tbd_count: countTbd(manual.body) });
      return;
    }
    sections.push({ id, num, title, body, citations, tbd_count: countTbd(body) });
  };

  // 0 — Cover / purpose summary
  add(
    "s00_cover",
    "0",
    "Cover letter / invitation to bid",
    `# Request for Proposal\n\n**Issue date:** ${maybe(ctx.process.issue_date)}\n**Response date:** ${responseDeadlineLine(ctx)}\n**Category:** ${pbk.label}\n**Buyer:** ${client}\n**Reference:** ${ctx.meta.pack_id}\n\n> This comprehensive pack was prepared for the stated category and reflects the context you provided. Every number in this pack is drawn from the context you supplied or a versioned category playbook; anything unconfirmed is marked ${TBD}. Legal documents remain attach-stubs.`,
    [client],
  );

  // 1 — Business context
  add(
    "s01_purpose",
    "1",
    "Purpose, background & context",
    `## Why we are running this RFP\n\n**Current state:** ${maybe(sanitizeContextText(ctx.problem.current_state))}\n\n**Current state constraints:** ${maybe(sanitizeContextText(ctx.problem.current_state_constraints))}\n\n**Business problem:** ${maybe(sanitizeContextText(ctx.problem.business_problem))}\n\n**Desired outcome:** ${maybe(sanitizeContextText(ctx.problem.desired_outcome))}\n\n**Success measures:**\n${lines(ctx.problem.success_metrics)}\n\n**Strategic importance:** ${sanitizeContextText(ctx.problem.strategic_importance) || METADATA_FALLBACKS.strategicImportance}\n\n**Incumbent:** ${ctx.problem.incumbent.exists === true ? "Incumbent in place" : ctx.problem.incumbent.exists === false ? "No incumbent" : METADATA_FALLBACKS.incumbent}${ctx.problem.incumbent.notes ? `\n- Notes: ${sanitizeContextText(ctx.problem.incumbent.notes)}` : ""}\n- Current contract end: ${sanitizeContextText(ctx.problem.incumbent.end_date) || METADATA_FALLBACKS.contractEnd}`,
    ["current_state", "current_state_constraints", "desired_outcome"],
  );

  // 2 — Scope
  add(
    "s02_scope",
    "2",
    "Scope of work & requirements",
    `## In scope\n${lines(ctx.scope.in_scope)}\n\n## Out of scope\n${lines(ctx.scope.out_of_scope)}\n\n## Operational scope\n- Sites: ${joinList(ctx.scope.sites)}\n- Volumes / throughput: ${maybe(summarizeVolumes(ctx.scope.volumes))}\n- Systems in scope: ${joinList(ctx.scope.systems)}`,
    ["in_scope", "out_of_scope"],
  );

  // 3 — Instructions to bidders
  const lead3 = ctx.stakeholders.procurement_lead?.trim() ?? "";
  const buyer3 = ctx.meta.buyer_entity?.trim() ?? "";
  const issuer3 =
    lead3 && buyer3 && lead3.toLowerCase() !== buyer3.toLowerCase()
      ? `${lead3} at ${buyer3}`
      : lead3 || buyer3;
  const issuer3WithEmail = ctx.stakeholders.contact_email?.trim()
    ? `${issuer3} (${ctx.stakeholders.contact_email.trim()})`
    : issuer3;
  add(
    "s03_response",
    "3",
    "Proposal response instructions",
    `## How this RFP will be issued and received\n\n- This RFP will be issued by ${maybe(issuer3WithEmail)}. Sensei does not invite suppliers to a portal; please do not look for a submission portal.\\n- Response deadline: ${submissionDeadlineSentence(ctx)}\\n- Suggested response time: ${suggestedResponseTime(isServices, subdomain)} (guidance — confirm at issue)\n- Submission channel: ${maybe(ctx.process.issue_method)}\n- Q&A: ${maybe(ctx.process.qa_method)}\n- Submission format: ${ctx.process.submission_format?.length ? ctx.process.submission_format.join(", ") : TBD}\n- Technical response page limit: ${ctx.process.page_limit_technical ? `${ctx.process.page_limit_technical} pages` : TBD}\n- Late submissions: ${ctx.process.late_policy === "reject" ? "rejected" : ctx.process.late_policy === "grace" ? "accepted at our discretion" : TBD}\n\n**Bidders must respond to every row of the compliance matrix (Appendix C) and complete the pricing template (Appendix J).** Unanswered rows will be treated as non-compliant.`,
    ["process"],
  );

  // 4 — Qualifications
  const quals = isServices
    ? `## Required experience\n\n- Demonstrate at least three comparable engagements for services of this kind, each of similar scale and complexity.\n- Evidence must confirm delivery of the service levels stated in Sections 5 and 8 (deliverable timeliness, SLA compliance, quality).\n- Name the accounts and explain how each maps to this scope.\n\n## References\n\n- Provide three references from engagements of comparable scale. Each reference must confirm the service levels claimed.\n\n## Capacity & financial standing\n\n- Confirm capacity to deliver the stated scope / go-live.\n- Provide financial statements or confirmation of financial standing.`
    : `## Required experience\n\n- Demonstrate at least three comparable engagements in contract logistics, each of similar scale and service scope.\n- Evidence must confirm delivery of the OTIF, inventory-accuracy and returns SLAs stated in Sections 5 and 8.\n- Name the accounts and explain how each maps to this scope, including any multi-site operation of comparable complexity.\n\n## References\n\n- Provide three references from engagements of comparable scale. Each reference must confirm the service levels claimed.\n\n## Capacity & financial standing\n\n- Confirm capacity for the stated volumes / go-live.\n- Provide financial statements or confirmation of financial standing.`;
  add(
    "s04_qualifications",
    "4",
    "Supplier qualifications & company information",
    quals,
    [],
  );

  // 5 — Compliance matrix
  const rows = matrixWithOverrides(ctx);
  const matrixText = rows
    .map((r) => `- **${r.id}** (${r.category}, ${r.mandatory ? "Mandatory" : "Desirable"}): ${r.requirement} — evidence: ${r.evidence} — maps to: ${r.maps_to}`)
    .join("\n");
  add(
    "s05_matrix",
    "5",
    "Detailed requirements & compliance matrix",
    `## Mandatory requirements\n\n${matrixText}\n\n> Summary in body; full matrix = Appendix C.`,
    ["matrix"],
  );

  // 6 — Solution narrative ask
  add(
    "s06_solution",
    "6",
    "Technical & solution proposal — instructions to the bidder",
    `## What we want you to describe\n\nPlease provide your proposed operating model and how it works with the systems in scope (${joinList(ctx.scope.systems)}).\n\n1. Describe how you will deliver each in-scope service.\n2. Provide a detailed implementation plan with a named go-live ${ctx.constraints.go_live ? `targeting ${ctx.constraints.go_live}` : "on a mutually agreed go-live date"}.\n3. Identify the top three risks to go-live and your mitigations.\n4. Explain how you will meet the mandatory compliance matrix rows.\n\nThis is a request for a solution, not a sales pitch. Do not describe generic capability that is not part of this engagement.`,
    ["scope"],
  );

  // 7 — Commercial
  add(
    "s07_commercial",
    "7",
    "Commercial proposal / pricing — instructions + TCO rules",
    `## Pricing\n\n- Complete the pricing template (Appendix J) without modifying its structure.
- All rates in ${maybe(ctx.meta.currency)}.
- Prices valid for ${ctx.commercial.price_validity_days ? `${ctx.commercial.price_validity_days} days` : TBD}.
- Payment terms: ${maybe(ctx.commercial.payment_terms)}.
- TCO requested over ${ctx.commercial.tco_years ? `${ctx.commercial.tco_years} years` : TBD}, including implementation and exit.
- Contract term: ${(() => { const term = normalizeContractTerm(ctx.constraints.contract_term_months); return term ? `${term} months` : TBD; })()}${ctx.constraints.contract_extensions ? ` (${ctx.constraints.contract_extensions})` : ""}.

**Configurable parameters:** Price validity and payment terms above are the buyer's stated defaults (validity ${ctx.commercial.price_validity_days ? `${ctx.commercial.price_validity_days} days` : TBD}, payment ${maybe(ctx.commercial.payment_terms)}) — adjust before issue if your commercial policy differs.

**Accessorials:** charges for anything not in the rate card must be published in the accessorials section.

${
  ctx.commercial.pricing_rules?.length
    ? `**Pricing & fee structures / accessorials stated in the briefing:** confirm your proposed treatment of each of the following:\n${ctx.commercial.pricing_rules.map((r) => `- ${sanitizeFinalText(r)}`).join("\n")}\n\n`
    : ""
}**Budget:** ${ctx.constraints.budget.publish_to_bidders ? maybe(ctx.constraints.budget.band || "published alongside the pricing template") : "not published by the buyer at this stage."}

${pbk.pricing_template.sheet ? `Please use the ${pbk.pricing_template.sheet} template (Appendix J).` : ""}`,
    ["commercial"],
  );

  // 8 — SLAs
  const slaLines = catalogueWithOverrides(ctx)
    .map((s) => `- **${s.metric}:** ${s.target}${s.mandatory ? " (mandatory)" : ""} — evidence: ${s.evidence}`)
    .join("\n");
  add(
    "s08_sla",
    "8",
    "Service levels & support",
    `## Service level catalogue\n\n${slaLines}\n\nAll SLAs listed above trace to a compliance matrix row. Where a target is ${TBD} you must propose a target with evidence.`,
    ["sla_catalogue"],
  );

  // 9 — Security (sub-domain adaptive for services; supply chain for physical categories)
  const security = isServices
    ? servicesSecurityText(ctx, subdomain)
    : `## Security requirements\n\n- Systems in scope: ${joinList(ctx.scope.systems)}\n- Personal data handled: ${
        ctx.legal_flags.personal_data === "yes"
          ? (ctx.legal_flags.personal_data_label || "Yes — GDPR-compliant handling required")
          : ctx.legal_flags.personal_data === "no"
            ? "No"
            : TBD
      }\n- SOC 2 / equivalent: ${ctx.legal_flags.soc2_required ? (ctx.legal_flags.soc2_unstated ? "Required / subject to standard vendor assessment" : "Required") : TBD}\n\n**Supply chain cyber:** suppliers must confirm vulnerability management and incident reporting for any integration with the systems in scope.`;
  add(
    "s09_security",
    "9",
    "Data security, privacy & compliance",
    security,
    ["legal_flags"],
  );

  // 10 — ESG (sub-domain adaptive for services; supply chain for physical categories)
  const esg = isServices
    ? servicesEsgText(subdomain)
    : `## Requirements\n\n- Confirm site labour standards and right of audit, covering direct and sub-contracted site staff.\n- ${ctx.legal_flags.hazmat ? "Hazmat / dangerous goods handled under the stated compliance regime." : "Report energy / emissions where the category makes this material."}\n- Confirm modern slavery statement and health & safety management.\n- Report environmental measures applicable to the category (emissions, waste, packaging).`;
  add(
    "s10_esg",
    "10",
    "Sustainability & ESG",
    `${esg}\n\n${
      pbk.legal_flags_hints?.length ? `**Category notes:** ${pbk.legal_flags_hints.join(" | ")}` : ""
    }`,
    ["esg"],
  );

  // 11 — Contract & legal
  const stubs = getLegalStubs(ctx);
  add(
    "s11_contract",
    "11",
    "Contract terms & conditions",
    stubs.map((s) => `## ${s.title}\n\n${s.body}`).join("\n\n"),
    ["legal"],
  );

  // 12 — Evaluation
  const sc = buildScorecard(ctx);
  const scLines = sc.map((r) => `- **${r.label}** (${r.weight}%)${r.how_scored ? `: ${r.how_scored}` : ""}`).join("\n");
  add(
    "s12_evaluation",
    "12",
    "Evaluation criteria & process",
    `## How proposals will be evaluated\n\n${scLines}\n\n**Weights total:** 100%\n\n*Note to Evaluator: Review and adjust the evaluation weight percentages below according to your organizational priorities before issuing to bidders.*\n\n**Scoring rubric:** scores use the 1 / 3 / 5 anchors set out in Appendix D — evidence quality, not prose volume.\n\n**Mandatory gates:** ${
      ctx.evaluation.mandatory_gates?.length
        ? ctx.evaluation.mandatory_gates.join(", ")
        : rows.some((r) => r.mandatory)
          ? `All ${rows.filter((r) => r.mandatory).length} mandatory compliance matrix rows (Appendix C)`
          : TBD
    } — failure of any gate disqualifies.\n\n**Process:** ${ctx.evaluation.interviews ? "Shortlisted suppliers will be invited to an interview / session." : "No interviews."} ${ctx.evaluation.negotiation ? "Terms and pricing are expected to be negotiated before award." : "No negotiation — award on stated TCO."}\n\nTarget number of bidders: ${ctx.evaluation.supplier_count_target ? maybe(ctx.evaluation.supplier_count_target) : "to be determined by the buyer."}.`,
    ["evaluation"],
  );

  // 13 — Appendices
  add(
    "s13_appendices",
    "13",
    "Appendices index",
    appendicesGuide(),
    ["appendices"],
  );

  // Admin boilerplate pass (clause library — non-destructive, existing sections only)
  applyAdminClauses(sections, ctx);

  // Rule 4 — universal playbook-default sanitization: sweep every section for
  // residual bracketed internal instructions ("[PLAYBOOK DEFAULT — confirm]",
  // "[TBD — confirm with insurance holder]", …) and replace them with clean,
  // vendor-facing phrasing so the final document ships no raw author notes.
  // Services-domain packs additionally purge any residual logistics/3PL
  // vocabulary (OTIF, contract logistics, hazmat, fleet, …) so the strict
  // domain boundary holds in the compiled document even if a playbook or
  // admin clause referenced it.
  for (const s of sections) {
    s.body = stripBoldMarkers(isServices ? purgeLogisticsTerms(sanitizeFinalText(s.body)) : sanitizeFinalText(s.body));
    s.tbd_count = countTbd(s.body);
  }

  // Quality
  const matrix = matrixWithOverrides(ctx);
  const weights = ctx.evaluation.weights;
  const slaDesc = catalogueWithOverrides(ctx).map((s) => s.target);
  const checks = runConsistencyChecks({
    ctx,
    matrix,
    sections: sections.map(({ id, body }) => ({ id, body })),
    weights,
    slaDescriptions: slaDesc,
    waivedGaps: opts?.waivedGaps,
  });
  const grade = gradePack(checks);
  const quality: QualityReport = {
    checks,
    pass_count: checks.filter((c) => c.severity === "pass").length,
    warn_count: checks.filter((c) => c.severity === "warn").length,
    block_count: checks.filter((c) => c.severity === "block").length,
  };

  return { sections, grade, quality };
}

// ─── Assemble a RFPack ────────────────────────────────────────────────────────

export function assemblePack(input: {
  id: string;
  name: string;
  client_name: string;
  category_id: CategoryId;
  created_by: string;
  created_by_email: string;
  workspace: "consultant" | "company";
  context: RFPContext;
  sourceDraftText?: string;
  sourceDraftName?: string;
  removedSections?: SectionId[];
  manualSections?: ManualSections;
  waivedGaps?: string[];
}): RFPack {
  const generated = generatePack(input.context, {
    skipSections: input.removedSections?.length ? new Set(input.removedSections) : undefined,
    manualSections: input.manualSections,
    waivedGaps: input.waivedGaps,
  });
  const rows = matrixWithOverrides(input.context);
  const scorecard = buildScorecard(input.context);
  const gaps = buildGaps(input.context);
  const pbk = getPlaybook(input.context.meta.category_id, input.context.meta.custom_category_name, ctxBrief(input.context));
  const anchors: RubricAnchor[] = pbk.rubric_anchors ?? [];
  const now = new Date().toISOString();

  return {
    id: input.id,
    pack_id: input.context.meta.pack_id || input.id,
    name: input.name,
    client_name: input.client_name,
    category_id: input.category_id,
    grade: generated.grade,
    created_at: now,
    updated_at: now,
    created_by: input.created_by,
    created_by_email: input.created_by_email,
    workspace: input.workspace,
    context: input.context,
    sections: generated.sections,
    artifacts: {
      compliance_matrix: rows,
      scorecard,
      rubric_anchors: anchors,
      pricing_template: pbk.pricing_template,
      gaps,
      vendor_questionnaire: vendorQuestionnaireWithAsks(input.context, pbk),
      sla_questionnaire: pbk.sla_questionnaire,
    },
    quality: generated.quality,
    stale: false,
    waived_gaps: [],
    removed_sections: input.removedSections,
    manual_sections: input.manualSections,
    source_draft_text: input.sourceDraftText ?? "",
    source_draft_name: input.sourceDraftName ?? "",
  };
}

export function isUnknownCategory(category: CategoryId): boolean {
  return !isKnownCategory(category);
}

export const CATEGORY_LABEL = categoryLabel;